<template>
  <div v-if="hasAssets">
    <div class="article-downloads">
      <div class="h3">
        {{ downloadsTitle }}
      </div>
      <ul class="icon-list icon-list--xxs">
        <li v-for="asset in downloads" :key="`asset-${asset.id}`">
          <a
            v-if="isBlockerDisabled || isDisabledForSubscriber"
            :aria-label="'Download ' + assetTitle(asset)"
            :href="asset.filename"
            :title="assetTitle(asset)"
            target="_blank"
            :download="assetTitle(asset)"
          >
            <i class="icon icon--xl">
              <Icon :name="assetIconName(asset)" />
            </i>
            <span class="title">
              {{ assetTitle(asset) }}
            </span>
          </a>

          <!-- when downloads are blocked -->
          <button
            v-else
            type="button"
            :aria-label="'Download ' + assetTitle(asset)"
            :title="assetTitle(asset)"
            @click="setDownloadFile(asset.filename)"
          >
            <i class="icon icon--xl">
              <Icon :name="assetIconName(asset)" />
            </i>
            <span class="title">
              {{ assetTitle(asset) }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <NewsletterDownloadBlocker
      v-if="selectedFile"
      :file="selectedFile.filename"
      @download-file="downloadFile"
      @cancel="closePopup"
    />
  </div>
</template>

<script setup lang="ts">
const { isBlockerDisabled } = useNewsletterContent()
const { isBlockerOpened, isDisabledForSubscriber } = storeToRefs(
  useNewsletterPopupStore(),
)

type DownloadAsset = {
  id: string
  filename: string
  title?: string
}
const props = defineProps<{
  downloads: DownloadAsset[] | undefined
}>()

const hasAssets = computed(() => !!props.downloads?.[0])
const assetTitle = (asset: DownloadAsset) =>
  asset.title ? asset.title : assetFilename(asset)
const assetFilename = (asset: DownloadAsset) =>
  asset.filename.split('/').pop()?.split('.')[0]
const assetExtension = (asset: DownloadAsset) => asset.filename.split('.').pop()
const assetIconName = (asset: DownloadAsset) => {
  const extension = assetExtension(asset)
  if (!extension) return 'IconFile'

  if (['jpeg', 'jpg', 'png', 'gif', 'svg', 'wepb'].includes(extension)) {
    return 'fa6-regular:file-image'
  }
  if (['pdf'].includes(extension)) {
    return 'fa6-regular:file-pdf'
  }
  if (['mp4', 'mpeg', 'webm', 'mov', 'avi'].includes(extension)) {
    return 'fa6-regular:file-video'
  }
  if (['mp3', 'wav'].includes(extension)) {
    return 'fa6-regular:file-audio'
  }
  return 'fa6-regular:file'
}

// sets title to plural only when more than 1 file has been added
const downloadsTitle = computed(() =>
  props.downloads?.length > 1 ? 'Downloads' : 'Download',
)

const selectedFile = ref<DownloadAsset | undefined>(undefined)

function setDownloadFile(filename: string) {
  selectedFile.value = props.downloads?.find(
    (asset) => asset.filename === filename,
  )
}

function downloadFile() {
  if (selectedFile.value !== undefined) {
    // Click link programmatically
    const link = document.getElementById('downloadFileAnchor')
    if (!!link && link.getAttribute('href') === selectedFile.value.filename) {
      link.click()
    } else {
      console.error('Invalid download link')
    }
  }
}

function closePopup() {
  selectedFile.value = undefined
}

watch(selectedFile, (value) => {
  if (value) {
    isBlockerOpened.value = true
  } else {
    isBlockerOpened.value = false
  }
})
</script>

<style lang="less" scoped>
.article-downloads {
  .rounded-sm();

  margin: @gutter-lg 0;
  background: @color-fill-pale;
  padding: @gutter-lg @gutter-sm @gutter-sm;
  max-width: @375px;

  .h3 {
    margin: 0 0 @gutter-sm @gutter-xs - @3px;
  }

  ul.icon-list {
    li {
      border-color: darken(@color-border, 8%);
      padding-right: @gutter-xxs;

      span {
        &.extension {
          .font-xxs();
        }
      }

      > a,
      button {
        display: flex;
        gap: @3px;
        align-items: center;
        background: none;
        border: none;
        cursor: pointer;
        text-align: left;
      }
    }
  }
}
</style>
